import { Box, Card, CardContent, Divider, Tabs, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { useState } from 'react';
import { useGetOne, useRecordContext, useTranslate } from 'react-admin';

import {
  GetB2bCompaniesIdEndpoint,
  GetB2bDashboardSavingsResponse,
} from '@Common/types/bo/b2b/companies';

import { mockSavings } from '@sh4res/bo-common/components/B2bCompanyDashboard/mock_data';
import {
  B2bCompanyDashboardProps,
  LegendLabel,
  PieCenterLabel,
} from '@sh4res/bo-common/components/B2bCompanyDashboard/utils';
import { MuiTab } from '@sh4res/bo-common/components/Tab';
import { formatCentsNumber } from '@sh4res/bo-common/utils';

const SLICE_COLORS = [
  '#5B5FC7',
  '#5BA3DC',
  '#5FA08C',
  '#7CA93A',
  '#C0A32C',
  '#DA9B2C',
  '#DF6F4C',
  '#CF6088',
  '#9C60C1',
];

const OTHER_SLICE_COLOR = '#ABABAB';

type LegendEntry = { label: string; value: number; color: string };

const SavingsLegendColumn = ({
  entries,
  startIndex,
  label,
}: {
  entries: LegendEntry[];
  startIndex: number;
  label: string;
}) => (
  // Fixed width so long fund names wrap
  <Box
    component="ul"
    aria-label={label}
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      width: 240,
      flexShrink: 0,
      listStyle: 'none',
      margin: 0,
      padding: 0,
    }}>
    {entries.map((entry, idx) => (
      <Box
        component="li"
        key={startIndex + idx}
        tabIndex={0}
        aria-label={`${entry.label}: ${formatCentsNumber(entry.value)}`}
        sx={{
          '&:focus': {
            outline: '1px solid',
            outlineOffset: '2px',
            borderRadius: '4px',
          },
        }}>
        <LegendLabel
          id={String(startIndex + idx)}
          color={entry.color}
          label={entry.label}
          value={formatCentsNumber(entry.value)}
        />
      </Box>
    ))}
  </Box>
);

export const SavingsSection = ({ useMock, resource }: B2bCompanyDashboardProps) => {
  const translate = useTranslate();
  const record = useRecordContext<GetB2bCompaniesIdEndpoint['Client']['ResponseBody']>();

  const { data: realData } = useGetOne<GetB2bDashboardSavingsResponse>(
    `${resource}/dashboard/savings`,
    { id: record?.id as number },
    { enabled: !!record },
  );

  const [tabValue, setTabValue] = useState(0);

  const data = useMock ? mockSavings.data : realData?.data;

  // Hide the component if there's no data or if total_savings is 0
  if (!record || !data || data.total_savings === 0) {
    return null;
  }

  const currentData = tabValue === 0 ? data.per_product : data.per_fund;

  // Keep the largest slices named and group everything beyond them into "Other"
  const namedItems = currentData.slice(0, SLICE_COLORS.length);
  const remaining = currentData.slice(SLICE_COLORS.length);
  const otherAmount = remaining.reduce((sum, item) => sum + item.amount, 0);

  const processedData =
    remaining.length === 0
      ? namedItems
      : [
          ...namedItems,
          {
            name: translate(`resources.${resource}.dashboard.savings.other`),
            amount: otherAmount,
          },
        ];

  const pieData = processedData.map((item, index) => ({
    id: item.name,
    value: item.amount * 100, // Convert to cents
    label: item.name,
    color: SLICE_COLORS[index] ?? OTHER_SLICE_COLOR,
  }));

  const legendSplitIndex = Math.ceil(pieData.length / 2);

  const legendLabel = `${translate(`resources.${resource}.dashboard.titles.savings`)} - ${
    tabValue === 0
      ? translate(`resources.${resource}.dashboard.savings.per_product`)
      : translate(`resources.${resource}.dashboard.savings.per_fund`)
  }`;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Card sx={{ margin: '2px 2px 15px 2px', borderRadius: '8px' }}>
      <CardContent sx={{ padding: 0, paddingBottom: '0px !important' }}>
        <Box
          sx={{
            padding: '20px 20px 15px 20px',
          }}>
          <Typography variant="h3" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {translate(`resources.${resource}.dashboard.titles.savings`)}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ padding: '0px 20px 0px 20px' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: 'auto',
                padding: '6px 16px',
                fontSize: '0.875rem',
              },
            }}>
            <MuiTab label={translate(`resources.${resource}.dashboard.savings.per_product`)} />
            <MuiTab label={translate(`resources.${resource}.dashboard.savings.per_fund`)} />
          </Tabs>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '60px',
            minHeight: 400,
            padding: '40px 60px',
          }}>
          <SavingsLegendColumn
            entries={pieData.slice(0, legendSplitIndex)}
            startIndex={0}
            label={legendLabel}
          />
          <Box sx={{ flexShrink: 0 }}>
            <PieChart
              series={[
                {
                  data: pieData,
                  innerRadius: 147,
                  outerRadius: 160,
                  paddingAngle: 2,
                  cornerRadius: 4,
                  valueFormatter: (entry) => formatCentsNumber(entry.value),
                },
              ]}
              height={350}
              width={400}
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              slotProps={{
                legend: {
                  hidden: true,
                },
              }}>
              <PieCenterLabel
                total={formatCentsNumber(data.total_savings * 100)}
                label={`resources.${resource}.dashboard.savings.total_savings`}
              />
            </PieChart>
          </Box>
          <SavingsLegendColumn
            entries={pieData.slice(legendSplitIndex)}
            startIndex={legendSplitIndex}
            label={legendLabel}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
